import {type RefObject, useCallback, useEffect, useLayoutEffect} from 'react'

import {canScrollFurther} from './can-scroll-further'

/** Which way a container is asked about: across its columns, or down its cards. */
export type ScrollEdgeAxis = 'inline' | 'block'

/**
 * Marks a scroll container with the directions it still has somewhere to go, as
 * `data-scroll-block-start` and the three others that name an axis and an end.
 *
 * The styled layer draws a fade at an edge that carries one, so a card that runs off the
 * container reads as continuing rather than as cut. It is the same question
 * `canScrollFurther` already answers for the arrow keys, asked of the container itself
 * rather than of whatever holds focus, so "there is more that way" is decided in one
 * place.
 *
 * **Written to the element rather than returned.** The answer changes as often as a
 * scroll offset does, and a cell re-rendered on every frame of a scroll gesture is a cell
 * whose card window and paging both pay for a decoration. `useBoardRoot` publishes its
 * scrollbar sizes through `style` for the same reason and by the same route: React never
 * sees these attributes, so it never removes them.
 *
 * Recomputed after every render as well as on scroll and resize, because what is *in* the
 * container is what most often changes the answer -- a page of cards arriving, the cell's
 * own window moving on -- and neither a scroll event nor a resize of the container reports
 * that. The write is skipped when the answer has not moved, which is nearly always: a
 * container that wrote on every pass would invalidate its own style between the reads its
 * neighbours are taking in the same layout pass.
 */
export function useScrollEdges(scrollRef: RefObject<HTMLElement | null>, axis: ScrollEdgeAxis): void {
  const update = useCallback(() => {
    const element = scrollRef.current
    if (element === null) return

    mark(element, `data-scroll-${axis}-start`, canScrollFurther(element, axis, 'start'))
    mark(element, `data-scroll-${axis}-end`, canScrollFurther(element, axis, 'end'))
  }, [axis, scrollRef])

  useLayoutEffect(update)

  useEffect(() => {
    const element = scrollRef.current
    if (element === null) return

    /*
      No observer answers this. `IntersectionObserver` would, but only against sentinel
      elements inside the scrolled content, and this package renders none: it is behaviour
      attached to an element the styled layer owns. In that layer the two sentinels would
      land inside the cell's own list of cards, which is a list a reader is counted
      through.
    */
    // eslint-disable-next-line github/prefer-observers
    element.addEventListener('scroll', update)

    // A container that has been made shorter than its contents has somewhere to go
    // without anything having been scrolled or rendered.
    const observer = new ResizeObserver(update)
    observer.observe(element)

    return () => {
      element.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [scrollRef, update])
}

function mark(element: HTMLElement, attribute: string, present: boolean): void {
  if (present === element.hasAttribute(attribute)) return

  if (present) {
    element.setAttribute(attribute, '')
  } else {
    element.removeAttribute(attribute)
  }
}
